import { Trans } from "@lingui/react";
import { Button } from "@warp-ds/elements/react/button";
import { memo, type RefObject } from "react";
import { useAppConfig } from "../../context/AppConfigProviderContext.tsx";
import { useImages } from "../../context/DealerDataProviderContext.tsx";
import { useGalleryState } from "../../context/GalleryStateProviderContext.tsx";
import { trackOpenGallery } from "../../utils/pulseHelper.ts";
import { TabId } from "../../utils/tabs.ts";
import { Icon } from "../Components/Icon.tsx";
import "./sections.css";

export const ImageGallery = memo(({ ref }: { ref: RefObject<HTMLDivElement | null> }) => {
  const images = useImages();
  const { setFullScreen, setImageIndex } = useGalleryState();
  const { isMobile } = useAppConfig();

  const openFullscreen = (index: number) => {
    trackOpenGallery();
    setImageIndex(index);
    setFullScreen(true);
  };

  if (!images || images.length < 3) {
    return null;
  }

  return (
    <section id={TabId.ImageGallery} className="section" ref={ref}>
      <h3 className="mb-16">
        <Trans id="dealerBrandingLayout.imageGallery.title" message="Image gallery" />
      </h3>
      <div className="relative grid grid-cols-2 grid-rows-[auto_auto] md:grid-cols-[2fr_1fr] gap-16">
        <div className="overflow-hidden rounded-8 border-1 hover:s-border-hover col-span-2 md:col-span-1 md:row-span-2 focus-within:focusable focus-within:transition-none active:outline-none aspect-16/10">
          <button type="button" className="bg-transparent focusable" onClick={() => openFullscreen(0)}>
            <span className="inset-0 absolute" aria-hidden="true" />
            <img src={images[0].url} className="object-cover object-center" alt="Main Gallery" />
          </button>
        </div>

        <div className="rounded-8 overflow-hidden border-1 hover:s-border-hover focus-within:focusable focus-within:transition-none active:outline-none aspect-16/10">
          <button type="button" className="bg-transparent focusable" onClick={() => openFullscreen(1)}>
            <span className="inset-0 absolute" aria-hidden="true" />
            <img src={images[1].url} className="w-full object-cover object-center" alt="Small Gallery One" />
          </button>
        </div>

        <div className="rounded-8 overflow-hidden border-1 hover:s-border-hover focus-within:focusable focus-within:transition-none active:outline-none aspect-16/10">
          <button type="button" className="bg-transparent focusable" onClick={() => openFullscreen(2)}>
            <span className="inset-0 absolute" aria-hidden="true" />
            <img src={images[2].url} className=" w-full object-cover object-center" alt="Small Gallery Two" />
          </button>
        </div>
        <Button
          variant="utility"
          className={`absolute left-16 top-16 sm:left-16 sm:bottom-16 sm:top-auto`}
          onClick={() => openFullscreen(0)}
        >
          <span className="flex items-center gap-8 text-caption">
            <Icon name="PictureStack" />
            {images.length} {isMobile ? "" : <Trans id="dealerBrandingLayout.imageGallery.images" message="images" />}
          </span>
        </Button>
      </div>
    </section>
  );
});

ImageGallery.displayName = "ImageGallery";
